import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon, CopyIcon, CheckIcon, ExternalLinkIcon, RotateCcwIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
const GOOGLE_REVIEW_URL = 'https://g.page/r/CVLx_QoYthXTEAI/review';
const AVY_LOGO_URL = 'https://avy.me/logo/avy-logo.png';
const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [{
    '@type': 'Question',
    name: 'Who is AVY® a good fit for?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'AVY® works best with founders, leadership teams and marketers at growing businesses — from ambitious startups to established multi-million revenue companies — who need sharper brand positioning, better digital presence, and design that drives commercial outcomes like shorter sales cycles and higher conversion.'
    }
  }, {
    '@type': 'Question',
    name: 'How does AVY® use client reviews?',
    acceptedAnswer: {
      '@type': 'Answer',
      text: 'We use reviews to genuinely improve how we deliver projects and to share honest client experiences as social proof on our website and marketing. We never edit your words beyond light proofreading for typos, we never publish your email address, and we never offer incentives for positive reviews.'
    }
  }]
};
export function Love() {
  useEffect(() => {
    document.title = 'Love | AVY®';
    'Client Reviews & Feedback | AVY® Brand, Design & Motion Studio | Birmingham, UK';
    const metaDesc = document.querySelector('meta[name="description"]');
    const descContent = 'Share your experience working with AVY® — a senior-led brand, design and motion studio in Birmingham, UK. Leave a testimonial or Google review to help other founders and marketers find the right creative partner.';
    if (metaDesc) {
      metaDesc.setAttribute('content', descContent);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = descContent;
      document.head.appendChild(meta);
    }
    const scriptId = 'avy-faq-schema';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(FAQ_SCHEMA);
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  // Form State
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [testimonial, setTestimonial] = useState('');
  // Captured Data for Success Screen
  const [submittedData, setSubmittedData] = useState({
    name: '',
    company: '',
    testimonial: '',
    rating: 5
  });
  const handleCopy = () => {
    if (!testimonial) return;
    navigator.clipboard.writeText(testimonial);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.currentTarget;
    const data = new FormData(form);
    setSubmittedData({
      name: data.get('name') as string,
      company: data.get('company') as string,
      testimonial: data.get('testimonial') as string,
      rating: rating
    });
    // Simulate API/n8n submission delay
    fetch('https://sprooce.app.n8n.cloud/webhook/d9cb66d1-4c46-4ed5-99e5-2a87fff266fc', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: data.get('name') as string,
        company: data.get('company') as string,
        role: data.get('role') as string,
        email: data.get('email') as string,
        testimonial: data.get('testimonial') as string,
        extraFeedback: data.get('extraFeedback') as string,
        rating: rating,
        consent: data.get('permission') === 'on'
      })
    }).catch(() => {}).finally(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
    });
  };
  const getTestimonialFontSize = (text: string) => {
    if (!text) return 'text-base';
    if (text.length > 400) return 'text-xs';
    if (text.length > 200) return 'text-sm';
    return 'text-base md:text-lg';
  };
  return <div className="min-h-screen bg-[#f7f7f7] text-[#0a0a0a] font-sans selection:bg-black selection:text-white py-12 md:py-20 px-4 sm:px-6 lg:px-8 flex flex-col" data-id="element-4">
      <div className="max-w-5xl mx-auto w-full flex-1" data-id="element-5">
        {/* Hero Section */}
        <div className="text-center mb-12 md:mb-16" data-id="element-6">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="flex flex-col items-center" data-id="element-7">
            <img src="https://avy.me/logo/avy-logo.png" alt="AVY®" className="h-20 md:h-24 mb-8 object-contain" data-id="element-8" />
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-gray-900 mb-4" data-id="element-9">
              Share your experience working with AVY®
            </h1>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-10">
              Your feedback helps us improve and win more of the right clients.
            </p>
          </motion.div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-11">
          {/* Section 1: Testimonial Form (Primary) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-8" data-id="element-12">
            <Card className="p-0 overflow-hidden" data-id="element-13">
              <AnimatePresence mode="wait" data-id="element-14">
                {!isSuccess ? <motion.div key="form" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0,
                y: -20
              }} className="p-6 md:p-8" data-id="element-15">
                    <div className="mb-8 border-b border-gray-100 pb-6" data-id="element-16">
                      <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-17">
                        Leave a review & feedback
                      </h2>
                      <p className="text-sm text-gray-500" data-id="element-18">
                        We use this feedback to improve how we work, deliver the
                        best service we can, and make better decisions about
                        future projects.
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6" data-id="element-19">
                      {/* Personal Info Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-20">
                        <div className="space-y-2" data-id="element-21">
                          <label htmlFor="name" className="block text-sm font-medium text-gray-900" data-id="element-22">
                            Name <span className="text-red-500" data-id="element-23">*</span>
                          </label>
                          <input type="text" id="name" name="name" required className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:border-black focus:ring-1 focus:ring-black outline-none transition-all bg-gray-50 focus:bg-white" placeholder="Jane Doe" data-id="element-24" />
                        </div>
                        <div className="space-y-2" data-id="element-25">
                          <label htmlFor="company" className="block text-sm font-medium text-gray-900" data-id="element-26">
                            Company / Brand{' '}
                            <span className="text-gray-400 font-normal" data-id="element-27">
                              (optional)
                            </span>
                          </label>
                          <input type="text" id="company" name="company" className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:border-black focus:ring-1 focus:ring-black outline-none transition-all bg-gray-50 focus:bg-white" placeholder="Acme Corp" data-id="element-28" />
                        </div>
                        <div className="space-y-2" data-id="element-29">
                          <label htmlFor="role" className="block text-sm font-medium text-gray-900" data-id="element-30">
                            Role / Job title{' '}
                            <span className="text-gray-400 font-normal" data-id="element-31">
                              (optional)
                            </span>
                          </label>
                          <input type="text" id="role" name="role" className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:border-black focus:ring-1 focus:ring-black outline-none transition-all bg-gray-50 focus:bg-white" placeholder="Founder & CEO" data-id="element-32" />
                        </div>
                        <div className="space-y-2" data-id="element-33">
                          <label htmlFor="email" className="block text-sm font-medium text-gray-900" data-id="element-34">
                            Email{' '}
                            <span className="text-gray-400 font-normal" data-id="element-35">
                              (optional, for follow up only)
                            </span>
                          </label>
                          <input type="email" id="email" name="email" className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:border-black focus:ring-1 focus:ring-black outline-none transition-all bg-gray-50 focus:bg-white" placeholder="jane@example.com" data-id="element-36" />
                        </div>
                      </div>

                      <div className="space-y-2" data-id="element-37">
                        <label htmlFor="project" className="block text-sm font-medium text-gray-900" data-id="element-38">
                          Project name or scope
                        </label>
                        <input type="text" id="project" name="project" className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:border-black focus:ring-1 focus:ring-black outline-none transition-all bg-gray-50 focus:bg-white" placeholder="e.g. Brand Identity Refresh" data-id="element-39" />
                      </div>

                      {/* Rating */}
                      <div className="space-y-3 pt-2" data-id="element-40">
                        <label className="block text-sm font-medium text-gray-900" data-id="element-41">
                          Overall rating
                        </label>
                        <div className="flex items-center gap-1" data-id="element-42">
                          {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => setRating(star)} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} className="p-1 focus:outline-none transition-transform hover:scale-110" data-id="element-43">
                              <StarIcon className={`w-8 h-8 ${star <= (hoverRating || rating) ? 'fill-black text-black' : 'fill-transparent text-gray-300'} transition-colors`} data-id="element-44" />
                            </button>)}
                        </div>
                      </div>

                      {/* Main Testimonial */}
                      <div className="space-y-2 pt-2" data-id="element-45">
                        <div className="flex items-end justify-between mb-2" data-id="element-46">
                          <label htmlFor="testimonial" className="block text-sm font-medium text-gray-900" data-id="element-47">
                            What was it like working with AVY®? What results or
                            shifts did you see?{' '}
                            <span className="text-red-500" data-id="element-48">*</span>
                          </label>
                          <button type="button" onClick={handleCopy} disabled={!testimonial} className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md transition-all whitespace-nowrap flex-shrink-0 ${copied ? 'bg-green-50 text-green-700' : testimonial ? 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900' : 'opacity-0 pointer-events-none'}`} data-id="element-49">
                            {copied ? <>
                                <CheckIcon className="w-3.5 h-3.5" data-id="element-50" />
                                Copied!
                              </> : <>
                                <CopyIcon className="w-3.5 h-3.5" data-id="element-51" />
                                Copy text
                              </>}
                          </button>
                        </div>
                        <textarea id="testimonial" name="testimonial" required rows={5} value={testimonial} onChange={e => setTestimonial(e.target.value)} className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm focus:border-black focus:ring-1 focus:ring-black outline-none transition-all bg-gray-50 focus:bg-white resize-y" placeholder="Share your experience..." data-id="element-52" />
                      </div>

                      {/* Extra Feedback */}
                      <div className="space-y-2" data-id="element-53">
                        <label htmlFor="extraFeedback" className="block text-sm font-medium text-gray-900" data-id="element-54">
                          If another business asked what it’s like working with
                          AVY®, what would you say?
                        </label>
                        <textarea id="extraFeedback" name="extraFeedback" rows={3} className="w-full rounded-lg border border-gray-200 px-4 py-3 text-sm focus:border-black focus:ring-1 focus:ring-black outline-none transition-all bg-gray-50 focus:bg-white resize-y" placeholder="I would tell them..." data-id="element-55" />
                      </div>

                      {/* Permission Toggle */}
                      <div className="pt-4 pb-2" data-id="element-56">
                        <label className="flex items-start gap-3 cursor-pointer group" data-id="element-57">
                          <div className="flex items-center h-5" data-id="element-58">
                            <input type="checkbox" name="permission" defaultChecked className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black cursor-pointer" data-id="element-59" />
                          </div>
                          <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors" data-id="element-60">
                            I'm happy for AVY® to use this on their website and
                            marketing.
                          </span>
                        </label>
                      </div>

                      {/* Submit Area */}
                      <div className="pt-4 border-t border-gray-100 flex flex-col items-start gap-3" data-id="element-61">
                        <Button type="submit" variant="primary" size="lg" className="w-full sm:w-auto min-w-[200px]" disabled={isSubmitting} data-id="element-62">
                          {isSubmitting ? <span className="flex items-center gap-2" data-id="element-63">
                              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-64" />
                              Submitting...
                            </span> : 'Submit testimonial'}
                        </Button>
                        <p className="text-xs text-gray-400" data-id="element-65">
                          We'll never share your email publicly.
                        </p>
                      </div>
                    </form>
                  </motion.div> : <motion.div key="success" initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="p-6 md:p-12 flex flex-col items-center text-center py-12" data-id="element-66">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-67">
                      <CheckIcon className="w-8 h-8 text-green-600" data-id="element-68" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3" data-id="element-69">
                      Thank you – this really helps us.
                    </h2>
                    <p className="text-gray-500 mb-10 max-w-md mx-auto" data-id="element-70">
                      We appreciate you taking the time to share your
                      experience. It means the world to our team.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto justify-center" data-id="element-71">
                      <Button variant="secondary" onClick={() => setIsSuccess(false)} className="w-full sm:w-auto" data-id="element-72">
                        <RotateCcwIcon className="w-4 h-4 mr-2" data-id="element-73" />
                        Back to leaving a review
                      </Button>
                      <Button variant="primary" onClick={() => window.open(GOOGLE_REVIEW_URL, '_blank')} className="w-full sm:w-auto" data-id="element-74">
                        Open Google Review page
                        <ExternalLinkIcon className="w-4 h-4 ml-2" data-id="element-75" />
                      </Button>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </Card>
          </motion.div>

          {/* Section 2: Google Review (Secondary) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="lg:col-span-4 space-y-6" data-id="element-76">
            <Card className="bg-gray-900 text-white border-none shadow-xl" data-id="element-77">
              <div className="mb-6" data-id="element-78">
                <div className="flex items-center gap-1 mb-5" data-id="element-79">
                  {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="w-6 h-6 text-yellow-400 fill-yellow-400" data-id="element-80" />)}
                </div>
                <h3 className="text-lg font-bold mb-2" data-id="element-81">
                  Leave a quick Google Review
                </h3>
                <p className="text-sm text-gray-300 leading-relaxed" data-id="element-82">
                  Short on time? You can leave a quick public review on Google
                  instead.
                </p>
              </div>

              <div className="space-y-4" data-id="element-83">
                <Button variant="secondary" className="w-full bg-white text-black hover:bg-gray-100 border-none justify-between group" onClick={() => window.open(GOOGLE_REVIEW_URL, '_blank')} data-id="element-84">
                  <span data-id="element-85">Open Google Review page</span>
                  <ExternalLinkIcon className="w-4 h-4 text-gray-400 group-hover:text-black transition-colors" data-id="element-86" />
                </Button>

                <p className="text-xs text-gray-400 text-center" data-id="element-87">
                  You can copy what you wrote above and paste it into Google.
                </p>
              </div>
            </Card>
          </motion.div>
        </div>

        <section aria-labelledby="about-avy-heading" className="mt-16" data-id="element-88">
          <div className="border-t border-gray-200 pt-10 pb-6 max-w-2xl" data-id="element-89">
            <h2 id="about-avy-heading" className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-4" data-id="element-90">
              About AVY®
            </h2>
            <p className="text-sm text-gray-500 leading-relaxed mb-3" data-id="element-91">
              AVY® Design Consultancy is a brand and web consultancy based in
              Birmingham, UK. We help founders and marketing leaders turn
              unclear brands and underperforming websites into clear, credible,
              sales‑ready digital experiences.
            </p>
            <p className="text-sm text-gray-500 leading-relaxed hidden md:block" data-id="element-92">
              Recent work includes repositioning an HVAC business that went on
              to secure its biggest contract to date with a leading Formula 1
              team.
            </p>
          </div>
        </section>

        <div className="pb-8 pt-8 text-left space-y-1.5" data-id="element-93">
          <p className="text-xs text-gray-400 tracking-widest uppercase" data-id="element-94">
            AVY® Design Consultancy 2021–2026
          </p>
          <p className="text-[11px] text-gray-400" data-id="element-95">
            AVY Consultancy Ltd, Company number: 12143361 registered in the UK.
          </p>
        </div>
      </div>
    </div>;
}